import React, { useState, useEffect, useCallback } from 'react';
import {
	Box,
	Paper,
	Typography,
	Stepper,
	Step,
	StepLabel,
	CircularProgress,
	Container,
} from '@mui/material';
import { EmployeeSelfEntryForm } from './EmployeeSelfEntryForm';
import { OnboardingDocumentViewer } from './OnboardingDocumentViewer';
import {
	getOnboardingPhaseTwoStatus,
	getOnboardingById,
	getOnboardingDocuments,
	OnboardingPhaseTwoStatus,
	OnboardingRecord,
} from 'core/API/onboarding';
import { IOnboardingDocument } from 'core/interfaces/onboardingInterface';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import style from './EmployeeOnboardingProgress.module.scss';
import { useAuth } from 'module/auth/context/AuthContext';
// import { Button } from 'components/ui/button';

/**
 * EmployeeOnboardingProgress
 *
 * This component is displayed when a user logs in with isOnboardingInProgress = true
 * It shows a stepper UI to track onboarding progress and allows the employee to fill in their information
 *
 * Flow:
 * 1. Call GET api/onboarding-phase-two/status to get progress/status
 * 2. Use onboarding IRI from status to fetch full onboarding record via /api/onboardings/{id}
 * 3. Fetch document templates via api/onboarding_document_templates
 * 4. Display basic info form (editable based on status) and document steps
 */
export default function EmployeeOnboardingProgress(): JSX.Element {
	const { logout } = useAuth();
	const [isLoading, setIsLoading] = useState(true);
	const [phaseTwoStatus, setPhaseTwoStatus] = useState<OnboardingPhaseTwoStatus | null>(null);
	const [onboardingRecord, setOnboardingRecord] = useState<OnboardingRecord | null>(null);
	const [_documentTemplates, setDocumentTemplates] = useState<IOnboardingDocument[]>([]);
	const [documentTemplatesList, setDocumentTemplatesList] = useState<IOnboardingDocument[]>([]);
	const [activeStep, setActiveStep] = useState(0);
	const { showToast } = useToastContext();

	const steps = ['Basic Information', 'Fill Documents', 'Complete'];

	/**
	 * Extract the onboarding ID from the IRI string (e.g., "/api/onboardings/1" -> 1)
	 */
	const extractIdFromIRI = (iri: string): number | null => {
		const match = iri.match(/\/(\d+)$/);
		return match ? parseInt(match[1], 10) : null;
	};

	const fetchOnboardingData = useCallback(async () => {
		setIsLoading(true);
		try {
			// Step 1: Get phase two status
			const status = await getOnboardingPhaseTwoStatus();
			if (!status) {
				showToast('error', 'Onboarding status not found');
				setIsLoading(false);
				return;
			}
			setPhaseTwoStatus(status);

			// Step 2: Extract onboarding ID from the status and fetch full onboarding record
			const onboardingId = extractIdFromIRI(status.onboarding);
			if (onboardingId) {
				const record = await getOnboardingById(onboardingId);
				if (record) {
					setOnboardingRecord(record);
				}
			}

			// Step 3: Fetch document templates
			const templates = await getOnboardingDocuments();
			setDocumentTemplates(templates);
			setDocumentTemplatesList(templates);

			// Determine active step based on status
			const basicInfoStatus = status.progress?.basic_info?.status;
			if (basicInfoStatus === 'completed' || basicInfoStatus === 'approved') {
				// Basic info is done, move to documents step
				const allDocsCompleted =
					status.progress?.documents?.length > 0 &&
					status.progress.documents.every(
						(doc) => doc.status === 'completed' || doc.status === 'approved'
					);
				if (allDocsCompleted) {
					setActiveStep(2); // Complete
				} else {
					setActiveStep(1); // Documents
				}
			} else {
				setActiveStep(0); // Basic Information
			}
		} catch (error) {
			console.error('Error fetching onboarding data:', error);
			showToast('error', 'Failed to load onboarding information');
		} finally {
			setIsLoading(false);
		}
	}, [showToast]);

	useEffect(() => {
		fetchOnboardingData();
	}, [fetchOnboardingData]);

	const handleFormSubmit = async () => {
		// Refresh the onboarding data after submission
		await fetchOnboardingData();
		showToast('success', 'Information saved successfully!');
	};

	if (isLoading) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '100vh',
					backgroundColor: '#f5f5f5',
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	if (!phaseTwoStatus) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '100vh',
					backgroundColor: '#f5f5f5',
				}}
			>
				<Paper sx={{ p: 4, maxWidth: 500 }}>
					<Typography variant="h6" sx={{ mb: 2 }}>
						Onboarding Record Not Found
					</Typography>
					<Typography variant="body1" color="text.secondary">
						We couldn&apos;t find your onboarding record. Please contact your manager or HR
						department.
					</Typography>
				</Paper>
			</Box>
		);
	}

	return (
		<div className={style.OnboardingProgressContainer}>
			<Container maxWidth="lg" sx={{ py: 4 }}>
				{/* Header */}
				<Paper sx={{ p: 3, mb: 4, backgroundColor: '#fff', position: 'relative' }}>
					{/* Logout link at top right for consistency */}
					<a
						href="#"
						onClick={(e) => {
							e.preventDefault();
							logout();
						}}
						style={{
							position: 'absolute',
							top: 16,
							right: 24,
							color: '#446388',
							textDecoration: 'underline',
							cursor: 'pointer',
							fontSize: 14,
						}}
					>
						Logout
					</a>
					<Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
						Welcome to Your Onboarding Journey!
					</Typography>
					<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
						Complete the following steps to finish your onboarding process.
					</Typography>

					{/* Stepper */}
					<Stepper activeStep={activeStep} alternativeLabel>
						{steps.map((label) => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
				</Paper>

				{/* Content */}
				{activeStep === 0 && onboardingRecord && (
					<EmployeeSelfEntryForm
						onboardingRecord={onboardingRecord}
						editable={phaseTwoStatus?.progress?.basic_info?.editable ?? true}
						onSubmit={handleFormSubmit}
					/>
				)}

				{activeStep === 1 && phaseTwoStatus && onboardingRecord && (
					<OnboardingDocumentViewer
						documentStatuses={phaseTwoStatus.progress?.documents || []}
						documentTemplates={documentTemplatesList}
						onboardingId={onboardingRecord.id}
						onRefresh={fetchOnboardingData}
						onAllDocumentsComplete={() => setActiveStep(2)}
					/>
				)}

				{activeStep === 2 && (
					<Paper sx={{ p: 4 }}>
						<Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
							Onboarding Complete!
						</Typography>
						<Typography variant="body1" color="text.secondary">
							You&apos;ve completed your onboarding process. Your manager will review your
							submission.
						</Typography>
					</Paper>
				)}
			</Container>
		</div>
	);
}
